import { useWorkActions } from '@/components/work/WorkActionsContext';
import { WorkActionCard } from '@/components/work/WorkActionCard';
import { Link } from 'react-router-dom';
import type { ActionListViewData } from '@shared/types/views';
import { cn } from '@/lib/utils';
import { CommunicationCue } from '@/components/views/atoms/CommunicationCue';
import { businessSupportingText, sweepVisibleHeading } from '@/lib/ui-editorial-sweeper';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { ExpandableText } from '@/components/ui/ExpandableText';
import { StatusBadge } from '@/components/views/atoms/StatusBadge';
import { formatDueDate } from '@/lib/format-due-date';
import { useState } from 'react';
import { actionFilters, filterActions, groupByDue, type ActionFilter } from './actions/action-list-interactions';

interface ActionListViewProps {
  hideTitle?: boolean;
  data: ActionListViewData;
}

const ACTIONABILITY_STYLES = {
  ready: 'text-emerald-800',
  blocked: 'text-[var(--domino-warning)]',
  advisory: 'text-domino-aubergine',
} as const;

export const ActionListView = ({ data, hideTitle }: ActionListViewProps) => {
  const surface = useWorkActions();
  // Filters and due-date groups arrange the items received; nothing is stored.
  const [filter, setFilter] = useState<ActionFilter>('all');
  const filters = actionFilters(data.items);
  const visibleItems = filterActions(data.items, filter);
  const groups = groupByDue(visibleItems, data.items);
  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div>
          {!hideTitle && <p className="text-lg font-semibold tracking-tight text-domino-ink">{sweepVisibleHeading(data.title)}</p>}
          {data.summary && data.summary.total > 0 && (
            <p className="mt-1 text-sm text-domino-inkSoft">
              {data.summary.total} action{data.summary.total > 1 ? 's' : ''}
              {data.summary.completed > 0 ? ` · ${data.summary.completed} faite${data.summary.completed > 1 ? 's' : ''}` : ''}
              {data.summary.high_priority > 0 ? ` · ${data.summary.high_priority} prioritaire${data.summary.high_priority > 1 ? 's' : ''}` : ''}
            </p>
          )}
          {businessSupportingText(data.description, ...data.items.map((item) => item.context ?? item.label)) && (
            <ExpandableText
              text={data.description ?? ''}
              collapsedLines={4}
              minCharactersToToggle={220}
              className="mt-2 max-w-3xl text-sm leading-6 text-domino-inkSoft"
            />
          )}
        </div>
      </div>

      {data.communication && (
        <CommunicationCue cue={data.communication} className="mt-4" />
      )}

      {filters.length > 0 && (
        <div role="group" aria-label="Filtrer les actions" className="mt-4 flex flex-wrap gap-1.5">
          {filters.map((option) => (
            <button key={option.id} type="button" aria-pressed={filter === option.id} onClick={() => setFilter(option.id)}
              className={cn('rounded-full border px-2.5 py-0.5 text-xs transition', filter === option.id
                ? 'border-domino-aubergine bg-domino-aubergine text-white'
                : 'border-domino-cardBorder bg-domino-white text-domino-ink hover:border-domino-aubergine')}>
              {option.label} <span className={filter === option.id ? 'text-white/80' : 'text-domino-inkSoft'}>{option.count}</span>
            </button>
          ))}
        </div>
      )}

      {(groups ?? [{ group: 'all', label: '', items: visibleItems }]).map((group) => (
      <section key={group.group} aria-label={group.label || undefined} className="mt-4">
        {group.label && (
          <h4 className={cn('flex items-baseline gap-2 border-b border-domino-cardBorder pb-1 text-xs font-semibold uppercase tracking-wide',
            group.group === 'overdue' ? 'text-[var(--domino-critical)]' : 'text-domino-inkSoft')}>
            {group.label}<span className="font-normal normal-case tracking-normal">{group.items.length}</span>
          </h4>
        )}
      <div className="space-y-3">
        {group.items.map((item) => {
          const work = !surface?.historical ? surface?.actions.find(candidate => candidate.context.sourceActionId === item.id && (!surface.sourceRunId || candidate.context.sourceRunId === surface.sourceRunId)) : null;
          if (work && surface?.actionsElsewhere) return null;
          if (work) return <div key={item.id} className="border-t border-domino-cardBorder py-5 first:border-t-0"><WorkActionCard work={work} compact /></div>;
          return (
          <div key={item.id} className="border-t border-domino-cardBorder py-4 first:border-t-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className={cn('text-[15px] font-medium leading-[22px]', item.status === 'done' || item.status === 'skipped' ? 'text-domino-inkSoft line-through decoration-domino-inkSoft/40' : 'text-domino-ink')}>
                  {/^\d+$/.test(item.label.trim()) ? "Recommandation à examiner" : item.label}
                </p>
              </div>
              {(item.priority === 'high' && item.status !== 'done' && item.status !== 'skipped') || item.status !== 'pending' ? (
                <div className="flex shrink-0 items-center gap-3">
                  {item.priority === 'high' && item.status !== 'done' && item.status !== 'skipped' && <StatusBadge status="high" label="Prioritaire" />}
                  {item.status !== 'pending' && <StatusBadge status={item.status} />}
                </div>
              ) : null}
            </div>

            {businessSupportingText(item.context, item.label) && (
              <ExpandableText
                text={item.context ?? ''}
                collapsedLines={4}
                minCharactersToToggle={220}
                className="mt-3 text-sm leading-6 text-domino-inkSoft"
              />
            )}

            {(item.entity || item.due || item.category) && (() => {
              const due = item.due ? formatDueDate(item.due) : null;
              const late = due?.overdue && item.status !== 'done' && item.status !== 'skipped';
              return (
                <p className="mt-1 text-[13px] leading-[18px] text-domino-inkSoft">
                  {[item.entity?.name, item.category].filter(Boolean).join(' · ')}
                  {(item.entity || item.category) && due ? ' · ' : ''}
                  {due && <span className={late ? 'font-medium text-domino-ink' : undefined}>
                    {`Échéance\u00a0: ${due.label}${late ? ', en retard' : ''}`}
                  </span>}
                </p>
              );
            })()}

            {item.actionability && item.itemAction && !surface && (
              <div className="mt-3">
                <div
                  className={cn(
                    'inline-flex text-[11px] font-medium',
                    ACTIONABILITY_STYLES[item.actionability.state],
                  )}
                >
                  {item.actionability.label}
                </div>
                {businessSupportingText(item.actionability.detail, item.label, item.context ?? "") && (
                  <p className="mt-2 text-xs leading-5 text-domino-inkSoft">
                    {item.actionability.detail}
                  </p>
                )}
                {item.actionability.evidenceRuns && item.actionability.evidenceRuns.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.actionability.evidenceRuns.map((run) => (
                      run.toolKey ? (
                        <Link
                          key={`${run.runId}:${run.strength}`}
                          to={`/tools/${run.toolKey}?run=${run.runId}`}
                          className="inline-flex items-center text-[11px] font-medium text-domino-aubergine transition hover:text-domino-prune"
                        >
                          {run.strength === 'exact' ? 'Voir le résultat lié' : 'Voir un résultat existant'}
                        </Link>
                      ) : (
                        <span
                          key={`${run.runId}:${run.strength}`}
                          className="inline-flex items-center text-[11px] font-medium text-domino-inkSoft"
                        >
                          {run.label}
                        </span>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}

            {item.itemAction && !surface && (
              <div className="mt-4">
                <ViewActionButton action={item.itemAction} variant="secondary" />
              </div>
            )}
          </div>
        ); })}
      </div>
      </section>
      ))}
    </div>
  );
};
